import React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { MdLock } from "react-icons/md";
import { usePermissions } from "../contexts/PermissionsContext";
import { firstAllowedPath, moduleForPath } from "../utils/modulePermissions";

/** Wraps the /admin page outlet. A role without View on the page's module (or
 * without Edit, for pages that only create or change records, e.g.
 * bookings/new) gets a "no access" panel instead of the page, so a hidden
 * sidebar link can't be bypassed by typing its URL. View-only access shows
 * the page with a notice; the writes themselves are refused in apiFetch. */
const ModuleAccessGate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const { loading, can } = usePermissions();

  if (loading) {
    return <p className="text-sm text-gray-500">Checking access…</p>;
  }

  const module = moduleForPath(pathname);
  if (!module) return <>{children}</>;

  const canView = (menuName: string) => can(menuName, "view");
  const allowed = canView(module.menuName) &&
    (!module.needsEdit || can(module.menuName, "edit"));

  if (!allowed) {
    const fallback = firstAllowedPath(canView);
    // Landing on /admin without dashboard access: go to the first page this
    // role can see rather than stopping at a lock screen.
    if (module.menuName === "dashboard" && fallback && fallback !== pathname) {
      return <Navigate to={fallback} replace />;
    }
    return (
      <div className="max-w-md mx-auto mt-16 bg-white rounded-2xl shadow-soft p-6 text-center">
        <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-2xl">
          <MdLock />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">No access</h2>
        <p className="mt-1 text-sm text-gray-600">
          {canView(module.menuName)
            ? "Your role can view this section but can't add or change records in it."
            : "Your role doesn't have access to this section."}{" "}
          Ask an administrator if you need it.
        </p>
        {fallback && fallback !== pathname && (
          <Link
            to={fallback}
            className="inline-block mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            Go to an allowed page
          </Link>
        )}
      </div>
    );
  }

  const canEdit = can(module.menuName, "edit");
  const canDelete = can(module.menuName, "delete");
  const notice =
    !canEdit && !canDelete
      ? "View only: your role can't add, change or delete records here."
      : !canEdit
      ? "Your role can't add or change records here."
      : !canDelete
      ? "Your role can't delete records here."
      : null;

  return (
    <>
      {notice && (
        <div className="mb-3 px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
          <MdLock className="shrink-0" />
          {notice}
        </div>
      )}
      {children}
    </>
  );
};

export default ModuleAccessGate;
